// store/v3/ui.ts — global loading flag + toast (SPEC §3 UiStoreApi, §9)
// Port of legacy `store-legacy/appState.ts` (loading / useToast / closeToast),
// minus the employee bits. The toast auto-closes after `timeout` ms.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { UiStoreApi, UiToast } from '~/types/v3/stores'

export type ToastIcon = UiToast['icon']

export interface ShowToastOptions {
  text: string
  color?: string
  icon?: ToastIcon
  /** ms before the toast auto-closes; default 2500 */
  timeout?: number
}

const DEFAULT_TIMEOUT_MS = 2500

/**
 * Legacy passes `bg-green-500` explicitly; keep one default per icon.
 * These strings are class names Tailwind has to find — `tailwind.config.js`
 * scans `store/**` for exactly that reason. The toast paints white text, so
 * every colour here has to be dark enough to read against.
 */
const COLOR_BY_ICON: Record<ToastIcon, string> = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-amber-600',
  info: 'bg-slate-700',
}

export const useUiStore = defineStore('v3-ui', () => {
  const loading = ref(false)
  const toast = ref<UiToast | null>(null)

  let nextId = 0
  let timer: ReturnType<typeof setTimeout> | null = null

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  /** Replaces any visible toast (legacy `useToast` closes the previous one first). */
  function showToast(options: ShowToastOptions): void {
    clearTimer()

    const icon: ToastIcon = options?.icon ?? 'info'
    nextId += 1

    toast.value = {
      text: options?.text ?? '',
      color: options?.color || COLOR_BY_ICON[icon],
      icon,
      id: nextId,
    }

    const timeout = typeof options?.timeout === 'number' && options.timeout > 0
      ? options.timeout
      : DEFAULT_TIMEOUT_MS
    const shownId = nextId

    timer = setTimeout(() => {
      timer = null
      // only close if no newer toast took over in the meantime
      if (toast.value?.id === shownId) toast.value = null
    }, timeout)
  }

  function closeToast(): void {
    clearTimer()
    toast.value = null
  }

  return {
    loading,
    toast,
    showToast,
    closeToast,
  } satisfies UiStoreApi
})

export type UiStore = ReturnType<typeof useUiStore>
